import { NavigationService } from "@/shared/services/NavigationService";
import { SystemView } from "@/shared/constants/Views.enum";
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import ServiceProvider from "./providers/ServiceProvider.vue";
import firebase from "firebase/app";
import "firebase/auth";
import {
  SNACKBAR_STYLE,
  STYLE_CLASSES
} from "@/shared/constants/StyleConstants";
import store from "./store";
import { NotificationService } from "./modules/Notification/services/NotificationService";
import { Subscription } from "./modules/Notification/models/Subscription";
import { Http } from "@status/codes";
import { NotificationMessage } from "./modules/Notification/models/NotificationMessage";
import {
  DATE_NOT_FOUND,
  FORM_CONSTANTS,
  FORM_MESSAGES,
  LOCAL_STORAGE
} from "@/shared/constants/FormConstants";
import {
  NOTIFICATION_ACCESS,
  NOTIFICATION_MESSAGES,
  NOTIFICATION_PERMISSIONS
} from "./modules/Notification/constants/NotificationConstants";
import SnackbarT from "@/shared/components/Snackbar/Snackbar";
import Snackbar from "@/shared/components/Snackbar/Snackbar.vue";
import ChatBot from "@/shared/components/ChatBot/ChatBot.vue";
import { Utils } from "@/shared/utils/utils";
import { mapGetters } from "vuex";
import { DashboardCard } from "./modules/Dashboards/models/DashboardCard";
import {
  DASHBOARD_PRINCIPAL,
  INFO_CARD_TEMPLATE
} from "./modules/Dashboards/constants/DashboardConstants";
import SearchPatient from "@/shared/components/SearchPatient/SearchPatient.vue";
import RouteMap from "@/shared/components/RouteMap/RouteMap.vue";
import {
  GENERAL_CONSTANTS,
  STORE_CONSTANTS
} from "./shared/constants/GeneralConstants";
import { DOCUMENT_STATES } from "./modules/Dashboards/AdminDashboard/constants/ActionConstants";
import { AxiosError } from "axios";

@Component({
  name: "App",
  components: {
    SearchPatient,
    RouteMap,
    ServiceProvider,
    Snackbar,
    ChatBot
  },
  computed: {
    ...mapGetters({
      dataNotifications: "getNotifications"
    })
  }
})
export default class App extends Vue {
  notificationService: NotificationService = new NotificationService();
  notifications: Array<NotificationMessage> = [];
  btnClassSecondary = STYLE_CLASSES.SECONDARY_BTN;
  btnClassPrimaryRound = STYLE_CLASSES.PRIMARY_ROUND_BTN;
  PAGE_TITLE = FORM_CONSTANTS.PAGE_TITLE;
  CLOSE = FORM_CONSTANTS.BUTTON.CLOSE_SESSION;
  hiddenBadge = false;
  closeOnContentClick = false;
  hover = true;
  fab = false;
  togglePanel = false;
  dataNotifications!: NotificationMessage[];

  get isExternalRole(): boolean {
    const role =
      this.$store.getters.getUserRole ||
      localStorage.getItem(LOCAL_STORAGE.ROLE) ||
      "";

    return role
      .split(",")
      .some((role: string) => role.trim().toUpperCase() === "EXTERNO");
  }

  get isExternalChatAssistant(): boolean {
    return this.$route.name === "ChatAssistant" && this.isExternalRole;
  }

  FORMS_MENU = FORM_CONSTANTS.MENU_BAR.FORMS;
  USERS_MENU = FORM_CONSTANTS.MENU_BAR.USERS;
  SERVICES_MENU = FORM_CONSTANTS.MENU_BAR.SERVICES;
  ORDERS_MENU = FORM_CONSTANTS.MENU_BAR.ORDERS;
  PATIENTS_MENU = FORM_CONSTANTS.MENU_BAR.PATIENTS;
  ADMIN_MENU = FORM_CONSTANTS.MENU_BAR.ADMIN;
  SCHEDULING_MENU = FORM_CONSTANTS.MENU_BAR.SCHEDULING;
  SCHEDULING_RULES = FORM_CONSTANTS.MENU_BAR.RULES;
  NO_NOTIFICATIONS = FORM_CONSTANTS.NO_NOTIFICATIONS;
  PANEL_VIEW = FORM_CONSTANTS.PANEL_VIEW;
  MY_CALENDAR = FORM_CONSTANTS.APP_BAR.OPTIONS.MY_CALENDAR;
  LOG_OUT = FORM_CONSTANTS.APP_BAR.OPTIONS.LOG_OUT;
  INFO_COLOR = SNACKBAR_STYLE.INFO.COLOR;
  NEW_VERSION_MESSAGE = GENERAL_CONSTANTS.TEXTS.NEW_VERSION;
  UPDATE_MESSAGE = GENERAL_CONSTANTS.TEXTS.UPDATE;

  listCard: DashboardCard[] = [INFO_CARD_TEMPLATE];

  registration: any = null;
  updateExists = false;
  intervalIdForConstantsUI!: number | null;

  async created() {
    document.addEventListener(
      GENERAL_CONSTANTS.TEXTS.SW_UPDATED,
      this.updateAvailable,
      { once: true }
    );
  }

  async mounted() {
    await this.getNotifications();
    window.setInterval(() => {
      this.getNotifications();
    }, 300000);
    await store.dispatch(STORE_CONSTANTS.FETCH_CONSTANTS_UI);
    this.intervalIdForConstantsUI = setInterval(() => {
      store.dispatch(STORE_CONSTANTS.FETCH_CONSTANTS_UI);
    }, 300000);

    store.commit("setUserId", localStorage.getItem(LOCAL_STORAGE.UID) || "");

    this.listCard = DASHBOARD_PRINCIPAL.filter(card => card.isPanelLateral);
  }

  beforeDestroy() {
    if (this.intervalIdForConstantsUI) {
      clearInterval(this.intervalIdForConstantsUI);
    }
  }

  @Watch("dataNotifications")
  dataNotificationsHandler() {
    this.notifications = this.dataNotifications;
  }

  @Watch("notifications")
  updateCounter() {
    this.hiddenBadge = this.notifications.length > 0 ? true : false;
  }

  @Watch("togglePanel")
  changePanelView() {
    store.commit("setPanelView", this.togglePanel);
  }

  async signOut() {
    await this.deleteSubscription();
    store.commit("chatassistant/clearAllActiveViewContexts");
    firebase.auth().signOut();
    localStorage.removeItem("TOKEN");
    localStorage.removeItem("UID");
    localStorage.removeItem("ROLE");
    localStorage.removeItem("VIEWS");
    localStorage.removeItem(LOCAL_STORAGE.SEARCHER);
    localStorage.removeItem("ACCESS");
    localStorage.removeItem("USERNAME");
    localStorage.removeItem("COUNTRY");
    localStorage.removeItem("AVATAR");
    store.commit("changeSession", false);
    store.commit("setRole", "");
    store.commit("setUserName", "");
    store.commit("setAvatar", "");
    NavigationService.navigateTo(SystemView.LOGIN);
  }

  async deleteSubscription() {
    if (
      Notification.permission === NOTIFICATION_PERMISSIONS.GRANTED &&
      FORM_CONSTANTS.GLOBAL_VARS.SERVICE_WORKER in navigator &&
      FORM_CONSTANTS.GLOBAL_VARS.PUSH_MANAGER in window
    ) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();

        if (subscription) {
          const uid = localStorage.getItem("UID");

          if (uid) {
            const data: Subscription = {
              subscription: JSON.parse(JSON.stringify(subscription)),
              userKey: uid
            };
            await this.notificationService.deleteSubscription(data);
          }
        }
      } catch {
        SnackbarT.popError(NOTIFICATION_MESSAGES.ERROR);
      }
    }
  }

  async deleteNotification(id: string, index: number) {
    try {
      const response = await this.notificationService.updateStateNotificationById(
        id,
        DOCUMENT_STATES.INACTIVE
      );
      if (response.code !== Http.Ok) {
        SnackbarT.popError(NOTIFICATION_MESSAGES.ERROR_DELETING);
      } else {
        this.notifications.splice(index, 1);
      }
    } catch (error) {
      if ((error as AxiosError).response?.status == Http.Forbidden)
        SnackbarT.popError(
          FORM_MESSAGES.FORBIDDEN_REQUEST + NOTIFICATION_ACCESS.PUT
        );
      else SnackbarT.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async getNotifications() {
    const uid = localStorage.getItem("UID");

    if (uid) {
      try {
        const response = await this.notificationService.getNotificationsByUser(
          uid
        );

        if (response.code === Http.Found && response.data !== undefined) {
          this.notifications = response.data;
          this.hiddenBadge = true;
        } else {
          this.notifications = [];
          this.hiddenBadge = false;
        }
      } catch (error) {
        this.hiddenBadge = false;
        if ((error as AxiosError).response?.status == Http.Forbidden)
          SnackbarT.popError(
            FORM_MESSAGES.FORBIDDEN_REQUEST + NOTIFICATION_ACCESS.GET
          );
        else SnackbarT.popError(NOTIFICATION_MESSAGES.ERROR_GETTING);
      }
    }
  }

  getNotificationsSize() {
    return this.notifications.length;
  }

  getHourFormat(dateStrint: string | number | undefined) {
    if (dateStrint != undefined) {
      const dateFormat = dateStrint as string;
      const getHourFormat = Number(dateStrint);
      return ` ${Utils.dateToyyyymmdd(dateFormat)} ${Utils.formatDateFromNumber(
        getHourFormat
      )}`;
    }
    return DATE_NOT_FOUND;
  }

  getUrlString(textMenssage: string | undefined) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    if (textMenssage != undefined) {
      const urls = textMenssage.match(urlRegex);
      if (urls == null) return;
      return urls[0];
    }
  }

  getWithoutUrl(textMenssage: string | undefined) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    if (textMenssage != undefined) {
      const textWithoutUrl = textMenssage.replace(urlRegex, "");
      if (textWithoutUrl == null) return;
      return textWithoutUrl;
    }
  }

  getUserHomePath() {
    return Utils.getUserHomePath();
  }

  actionClickButton(card: DashboardCard) {
    const newRoute = Utils.calculeNewRoute(card);
    if (newRoute) {
      NavigationService.navigateByPath(newRoute);
    }
  }

  get fullPatch() {
    return this.$route?.fullPath.replace("/", "_") ?? "";
  }

  updateAvailable(event: any) {
    this.registration = event.detail;
    //this.updateExists = true;
  }

  refreshApp() {
    this.updateExists = false;
    if (!this.registration || !this.registration.waiting) return;
    this.registration.waiting.postMessage({
      type: GENERAL_CONSTANTS.TEXTS.SKIP_WAITING
    });
  }
}
